import { notification } from 'antd';
import React from 'react';

/**
 * Turn axios / API errors into user-friendly messages (permissions, status codes, etc.)
 * @param {unknown} error
 * @param {(key: string, defaultValue?: string) => string} t
 * @returns {string}
 */
export function formatApiError(error, t = (key) => key) {
  if (!error) {
    return t('An error occurred. Please try again.');
  }

  if (typeof error === 'string') {
    return error;
  }

  const status = error?.response?.status;
  const data = error?.response?.data;

  if (status === 401) {
    return t('You are not signed in. Please log in again.');
  }
  if (status === 403) {
    const policy = data?.policy;
    const base = t('You do not have permission to access this resource. Please contact your administrator.');
    return policy ? `${base}\nMissing required policy: ${policy}` : base;
  }
  if (status === 404) {
    return t('The requested resource was not found.');
  }
  if (status === 413) {
    return t('The file is too large.');
  }
  if (status >= 500) {
    const detail = extractResponseDetail(data);
    if (detail) {
      return detail;
    }
    return t('A server error occurred. Please try again later.');
  }

  const detail = extractResponseDetail(data);
  if (detail) {
    return detail;
  }

  const message = error?.message || '';
  if (/status code 401/i.test(message)) {
    return t('You are not signed in. Please log in again.');
  }
  if (/status code 403/i.test(message)) {
    const policy = error?.response?.data?.policy;
    const base = t('You do not have permission to access this resource. Please contact your administrator.');
    return policy ? `${base}\nMissing required policy: ${policy}` : base;
  }
  if (/status code 404/i.test(message)) {
    return t('The requested resource was not found.');
  }
  if (/status code 5\d{2}/i.test(message)) {
    return t('A server error occurred. Please try again later.');
  }

  if (message) {
    return message;
  }

  return t('An error occurred. Please try again.');
}

/**
 * Flatten string | string[] | axios error into unique display lines (deduped, order preserved).
 * @param {unknown} errorPayload
 * @param {(key: string) => string} [t]
 * @returns {string[]}
 */
export function normalizeApiErrorParts(errorPayload, t = (key) => key) {
  if (errorPayload == null || errorPayload === '') return [];

  const other = [];
  const policies = [];

  const push = (value) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach(push);
      return;
    }
    if (typeof value === 'object' && (value.response || value.message)) {
      push(formatApiError(value, t));
      return;
    }
    let text = String(value).trim();
    if (!text) return;

    text = text.replace(/Missing required policy:\s*([^\n,—]+)/gi, (_, name) => {
      policies.push(`Missing required policy: ${String(name).trim()}`);
      return '\n';
    });

    text
      .split(/\n|[—,]/)
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((s) => {
        if (/^Missing required policy:/i.test(s)) policies.push(s);
        else other.push(s);
      });
  };
  push(errorPayload);

  const dedupe = (lines) => {
    const seen = new Set();
    const out = [];
    for (const line of lines) {
      const key = line.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(line);
    }
    return out;
  };

  return [...dedupe(other), ...dedupe(policies)];
}

/** @param {unknown} errorPayload @param {(key: string) => string} [t] */
export function normalizeApiErrorText(errorPayload, t = (key) => key) {
  return normalizeApiErrorParts(errorPayload, t).join(' — ') || null;
}

function extractResponseDetail(data) {
  if (!data) return null;

  const parts = [];

  if (typeof data === 'string') {
    return data;
  }

  if (data.detail) {
    parts.push(typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail));
  }
  if (data.message && data.message !== data.detail) {
    parts.push(data.message);
  }
  if (data.error && data.error !== data.detail) {
    parts.push(typeof data.error === 'string' ? data.error : JSON.stringify(data.error));
  }
  if (data.errors && typeof data.errors === 'object') {
    Object.entries(data.errors).forEach(([key, value]) => {
      const text = Array.isArray(value) ? value[0] : value;
      if (text) parts.push(`${key}: ${text}`);
    });
  }

  return parts.length > 0 ? parts.join(' — ') : null;
}

/** @param {{ error?: string, policy?: string }} result */
export function showAccessDenied(result, t = (key) => key) {
  const parts = normalizeApiErrorParts(
    result?.error || (result?.policy ? `Missing required policy: ${result.policy}` : null),
    t
  );
  const policies = parts.filter((p) => /^Missing required policy:/i.test(p));
  const other = parts.filter((p) => !/^Missing required policy:/i.test(p));
  const contact = t(
    'You do not have permission to access this resource. Please contact your administrator.'
  );
  notification.error({
    message: t('Authorization Error'),
    description: (
      <div style={{ wordBreak: 'break-word' }}>
        <div>{other.length ? other.join(' — ') : contact}</div>
        {policies.length > 0 ? (
          <div style={{ marginTop: 8, opacity: 0.85 }}>
            {policies.map((p) => p.replace(/^Missing required policy:\s*/i, '')).join(', ')}
          </div>
        ) : null}
      </div>
    ),
    placement: 'topRight',
    duration: 6,
  });
}

/** @param {{ error?: string, policy?: string }|null|undefined} result @returns {boolean} true if access denied was shown */
export function ifAccessDenied(result, t = (key) => key) {
  if (!result?.error && !result?.policy) return false;
  showAccessDenied(result, t);
  return true;
}

/** Show Redux error payload (string | string[] from formatErrorMessage). */
export function showApiError(errorPayload, t = (key) => key) {
  const text = normalizeApiErrorText(errorPayload, t);
  if (!text) return;
  if (/permission|403|policy|authorization/i.test(text)) {
    showAccessDenied({ error: text }, t);
    return;
  }
  notification.error({
    message: t('Error'),
    description: text,
    placement: 'topRight',
    duration: 5,
  });
}
